/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { ServiceRegistry } from '../utils/ServiceRegistry';
import { StatusManager } from '../utils/StatusManager';
import { ErrorHandler } from '../utils/ErrorHandler';
import { StatusMessages } from '../utils/constants';
import type { DomController } from './DomController';
import type { DriveService } from './DriveService';
import type { SettingsService } from './SettingsService';
import type { NoteService } from './NoteService';

/**
 * Manages all Google authentication and authorization flows.
 */
export class AuthService {
    private statusManager: StatusManager;
    
    // Service registry getters
    private get dom() { return ServiceRegistry.get<DomController>('dom'); }
    private get driveService() { return ServiceRegistry.get<DriveService>('drive'); }
    private get settingsService() { return ServiceRegistry.get<SettingsService>('settingsService'); }
    private get noteService() { return ServiceRegistry.get<NoteService>('noteService'); }
    
    private actionAfterAuth: (() => void) | null = null;
    public onLoginSuccess: (() => void) | null = null;
    
    constructor() {
        this.statusManager = new StatusManager(this.dom);
        this.bindEventListeners();
    }
    
    private bindEventListeners(): void {
        if (this.dom.signInButton) {
            this.dom.signInButton.addEventListener('click', () => this.handleSignIn());
        }
        if (this.dom.loginGateSignInButton) {
            this.dom.loginGateSignInButton.addEventListener('click', () => this.handleSignIn());
        }
        if (this.dom.signOutButton) {
            this.dom.signOutButton.addEventListener('click', () => this.handleSignOut());
        }
    }
    
    public setActionAfterAuth(action: (() => void) | null): void {
        this.actionAfterAuth = action;
    }
    
    public async initGoogleDrive(isProduction = false): Promise<void> {
        try {
            const onAuthSuccess = async (tokenResponse: any) => {
                if (tokenResponse.error) {
                    ErrorHandler.handle('google auth error', tokenResponse.error, this.dom, false);
                    this.statusManager.error('Google Drive authorization failed');
                    this.actionAfterAuth = null;
                    if(this.driveService.currentUser) this.handleSignOut();
                    return;
                }
                (window as any).gapi.client.setToken(tokenResponse);

                // Store token for persistence across page reloads
                this.driveService.storeToken(tokenResponse);

                this.statusManager.loading('Fetching profile');
                try {
                    const profile = await this.driveService.getUserProfile();

                    this.dom.showLoggedInState(profile.name, profile.picture);
                    this.statusManager.custom(`Welcome, ${profile.name.split(' ')[0]}!`);

                    // Notify AppController that authentication succeeded with user profile
                    if (this.onLoginSuccess) {
                        this.onLoginSuccess();
                    }
                } catch (e) {
                    ErrorHandler.handle('fetching user profile', e, this.dom, false);
                    this.handleSignOut();
                    return;
                }
    
                await this._syncSettingsWithDrive();
    
                if (this.onLoginSuccess) {
                    this.onLoginSuccess();
                }

                if (this.actionAfterAuth) {
                    this.actionAfterAuth();
                    this.actionAfterAuth = null;
                }
            };
    
            await this.driveService.init(onAuthSuccess);
            
            this.dom.saveToDriveButton.disabled = false;
            this.dom.saveToDriveButton.title = 'Save to Google Drive';

            if (isProduction) {
                this.attemptSilentSignIn();
            }
        } catch (error) {
            this.statusManager.error((error as Error).message);
            ErrorHandler.handle('initializing Google Drive Service', error, this.dom, false);
        }
    }

    private attemptSilentSignIn(): void {
        if (this.driveService.googleTokenClient) {
            // First check if we already have a valid token
            const currentToken = (window as any).gapi.client.getToken();
            if (currentToken && currentToken.access_token) {
                ErrorHandler.log('silent sign-in', 'Valid token already exists, no need for silent sign-in');
                return;
            }

            // Attempt silent sign-in to restore authentication
            this.driveService.googleTokenClient.requestAccessToken({
                prompt: 'none',
                state_cookie_domain: window.location.hostname
            });
        }
    }

    public async requestAuthAndRun(action: () => void): Promise<void> {
        if (!this.driveService.isGoogleAuthReady || !this.driveService.googleTokenClient) {
            this.statusManager.error('Google Drive client is not ready. Please wait.');
            return;
        }
    
        if ((window as any).gapi.client.getToken() === null) {
            this.statusManager.loading('Connecting to Google Drive');
            this.actionAfterAuth = action;
            this.driveService.googleTokenClient.requestAccessToken({ prompt: '' });
        } else {
            action();
        }
    }
    
    /* public handleSignIn(): void {
        if (this.driveService.googleTokenClient) {
            try {
                this.driveService.googleTokenClient.requestAccessToken({
                    prompt: 'consent',
                    state_cookie_domain: window.location.hostname
                });
            } catch (error) {
                ErrorHandler.handle('OAuth request', error, this.dom, false);
                this.statusManager.error('Authentication request failed. Please try again.');
            }
        } else {
            this.statusManager.error('Google Drive client is not ready.');
        }
    } */

    public handleSignIn(): void {
        if (this.driveService.googleTokenClient) {
            try {
                this.driveService.googleTokenClient.requestAccessToken({
                    // CHANGE: Remove 'consent'. 
                    // 'select_account' lets the user switch accounts if they want,
                    // but skips the permissions screen if already granted.[**Cypress**](https://www.cypress.io/)
                    prompt: 'consent', 
                    state_cookie_domain: window.location.hostname
                });
            } catch (error) {
                ErrorHandler.handle('OAuth request', error, this.dom, false);
                this.statusManager.error('Authentication request failed. Please try again.');
            }
        } else {
            this.statusManager.error('Google Drive client is not ready.');
       }
    }
    
    private handleSignOut(): void {
        this.driveService.signOut();
        this.dom.showLoggedOutState();
        this.statusManager.custom('Signed out. Local data secured.');

        // Clear all local data on sign out for security
        this.noteService.clearAllLocalData();

        // Show login gate to prevent access to any remaining data
        this.dom.loginGate.classList.remove('hidden');
    }
    
    private async _syncSettingsWithDrive(): Promise<void> {
        try {
            const driveSettings = await this.driveService.getSettingsFromDrive();
            if (driveSettings) {
                await this.settingsService.applySettings(driveSettings);
                this.noteService.restartAutosaveTimer();
                this.statusManager.success('Settings synced from Google Drive');
            } else {
                const localSettings = this.settingsService.getAllSettings();
                await this.driveService.saveSettingsToDrive(localSettings);
                this.statusManager.success('Local settings saved to Google Drive');
            }
        } catch (error) {
            ErrorHandler.handle('syncing settings with Google Drive', error, this.dom, false);
            this.statusManager.error('Could not sync settings with Google Drive');
        }
    }
}